import { z } from 'zod';
/**
 * The error envelope a Cashier Profile BFF returns on any non-2xx response.
 *
 * Shaped like PaymentFlow's own envelope (SAD §8) on purpose — a UI that already renders one
 * should not need a second renderer — but with an **open** `code` space, which is the substantive
 * difference and the reason this schema exists rather than re-exporting `ErrorEnvelopeSchema`
 * from `@pf/contracts`.
 *
 * PaymentFlow's codes are a closed enum because PF owns every one of them. A BFF is somebody
 * else's system: it legitimately fails for reasons PaymentFlow has no opinion about
 * (`INSUFFICIENT_FUNDS` against the website's own ledger, `NO_SESSION`, a KYC hold), and a
 * profile that forced those into PF's enum would either lie about the cause or force every
 * implementer to fork the enum. So `code` is a non-empty string, and the codes below are the ones
 * this profile *names* — an implementation may add its own.
 */
/**
 * Error codes the profile assigns a meaning to.
 *
 * An implementation SHOULD use these where they apply, because the reference UI maps them to
 * specific recovery affordances (re-authenticate, top up, pick a different amount). Any other
 * string is valid and is rendered as a plain message.
 */
export const CASHIER_ERROR_CODES = {
    /** No customer session, or it lapsed. The UI sends the customer back to sign-in. */
    NO_SESSION: 'NO_SESSION',
    /** Credentials rejected at `POST /bff/auth/login`. */
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    /** The identity is real but belongs to a different plane (e.g. an operator account). */
    WRONG_PLANE: 'WRONG_PLANE',
    /** The request body failed the profile's schema. */
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    /** No such deposit, withdrawal or activity row for *this* customer. */
    NOT_FOUND: 'NOT_FOUND',
    /** The website's own ledger cannot cover the requested withdrawal. */
    INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
    /** The action is not legal from the resource's current state. */
    ILLEGAL_TRANSITION: 'ILLEGAL_TRANSITION',
    /** No withdrawal is available at the requested amount (PF's `OUT_OF_STOCK`, surfaced). */
    OUT_OF_STOCK: 'OUT_OF_STOCK',
    /** The BFF reached PaymentFlow and PaymentFlow refused or failed. `details.pf_code` carries PF's own code. */
    UPSTREAM_ERROR: 'UPSTREAM_ERROR',
};
/** The error envelope every non-2xx profile response carries. */
export const CashierErrorEnvelopeSchema = z
    .object({
    error: z
        .object({
        /** A named code from {@link CASHIER_ERROR_CODES}, or an implementation's own. */
        code: z.string().min(1),
        /** Coarse category, so a UI can decide between "retry" and "tell the user". */
        type: z.enum(['authentication', 'authorization', 'validation', 'conflict', 'business_rule', 'transient']),
        /** Human-readable, and safe to show a customer. Never a stack trace. */
        message: z.string().min(1),
        /** True when the identical request may succeed later without the customer changing anything. */
        retryable: z.boolean(),
        /** Correlation id the customer can quote to support. `null` when the BFF has none. */
        request_id: z.string().min(1).nullable(),
        /** Free-form context. `pf_code` is conventional on `UPSTREAM_ERROR`. */
        details: z.record(z.string(), z.unknown()).optional(),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=errors.js.map